import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import { Grid, Hidden, Button, CircularProgress, InputAdornment } from '@material-ui/core';
import ZoomInIcon from '@material-ui/icons/ZoomIn';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import appsListStatuses from '@/constants/app';
import {
  ROUTE_DISCOVER,
  ROUTE_DISCOVER_SEARCH,
  ROUTE_DISCOVER_APP,
  ROUTE_SHOWS_AND_MOVIES_SHOW,
  ROUTE_SHOWS_AND_MOVIES_MOVIE,
  ROUTE_DISCOVER_CHANNEL,
  ROUTE_PARTNER_DISCOVER,
} from '@/constants/routes';
import { LOCATIONS_DISCOVER_PAGE, LOCATIONS_ON_CARD } from '@/constants/locations';
import { MARKETPLACE_PAGE } from '@/constants/swimlanes';

// UTILITY
import { routeEnhance, getLink, pagePartnerProtector } from '@/utility/routes';
import {
  analyticsInternalLinks,
  analyticsPageLoaded,
  analyticsUpdateAppListStatus,
  customAnalyticsEvent,
} from '@/utility/analytics';
import { getOrgSchema } from '@/utility/orgSchemas/orgSchemas';

// STORE
import {
  DiscoverActions,
  AppDetailsActions,
  OnboardingActions,
  UserActions,
  SwimlanesActions,
} from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import PageWithFilters from '@/hoc/PageWithFilters';
import withMediaQuery from '@/hoc/withMediaQuery';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import ContentHero from '@/components/Heroes/ContentHero';
import SearchAutoComplete from '@/components/Form/SearchAutoComplete';
import Swimlanes from '@/components/Swimlanes';
import PartnerInternetOffer from '@/components/PartnerInternetOffer';

// HELP
import Filters from './help/Filters';

// STYLES
import { Wrapper } from './styles';

const types = [
  { text: 'common.free', value: 'free' },
  { text: 'common.paid', value: 'paid' },
];

const filterInitialParams = {
  genres: [],
  channels: [],
  keywords: [],
  type: ['paid', 'free'],
  devices: '',
  show_id: '',
  movie_id: '',
};

class StreamingMarketplace extends PageWithFilters {
  constructor(props) {
    super(props);

    this.state = {
      openFilters: false,
      search: '',
      params: filterInitialParams,
    };
    this.debouncedSearch = debounce(this.getDiscoverSearch, 700);
  }

  componentDidMount() {
    const { isAuth, partner, getDiscoverFilters, getDiscoverCollections, userGetApps } = this.props;

    pagePartnerProtector(partner, ['use_marketplace']);

    if (isAuth) {
      userGetApps({ status: appsListStatuses.have });
    }

    getDiscoverFilters();
    getDiscoverCollections();
  }

  componentDidUpdate(prevProps) {
    const { partner, isAuth, getDiscoverCollections, userGetApps } = this.props;

    pagePartnerProtector(partner, ['use_marketplace']);

    if (prevProps.isAuth !== isAuth) {
      if (isAuth) {
        userGetApps({ status: appsListStatuses.have });
      }
      getDiscoverCollections();
    }
  }

  componentWillUnmount() {
    const { cleanDiscoverSearch, swimlanesClear } = this.props;

    cleanDiscoverSearch();
    swimlanesClear();
  }

  getDiscoverSearch = search => {
    const { partner, getDiscoverSearch, isSimpleView } = this.props;
    const exclude_content = isSimpleView ? { exclude_content: true } : {};

    return getDiscoverSearch({
      ...exclude_content,
      search_string: search.trim(),
      partner: partner?.partner_slug || '',
    });
  };

  handleChange = (name, value) => {
    const { params } = this.state;
    const data = {
      [name]: value,
    };

    this.setState({ params: { ...params, ...data } });
  };

  onChangeSearch = search => {
    this.setState({ search });
  };

  handleSearch = (event, value, reason) => {
    const { cleanDiscoverSearch } = this.props;

    if (event?.keyCode === 13 && reason === 'reset') {
      return;
    }

    if (reason === 'input' || reason === 'clear') {
      this.onChangeSearch(value);

      if (value) {
        this.debouncedSearch(value);
      } else {
        cleanDiscoverSearch();
      }
    }
  };

  handleSelect = (event, value, reason, handleOpen) => {
    const { params } = this.state;
    const {
      router: { navigate },
      searchPrevString,
    } = this.props;

    if (reason === 'clear') {
      this.onChangeSearch('');
      return;
    }

    if (reason === 'select-option') {
      const type = value?.type || '';
      let routeEnhanced;
      const options = {
        state: {
          location: LOCATIONS_DISCOVER_PAGE,
        },
      };

      switch (type) {
        case 'app':
          routeEnhanced = routeEnhance(getLink(ROUTE_DISCOVER_APP, { slug: value.slug }), options);

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: routeEnhanced.pathname,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          navigate(routeEnhanced);
          break;
        case 'channel':
          routeEnhanced = routeEnhance(
            getLink(ROUTE_DISCOVER_CHANNEL, {
              slug: value.slug || value.id,
            }),
            options,
          );

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: routeEnhanced.pathname,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          navigate(routeEnhanced);
          break;
        case 'keyword':
          routeEnhanced = routeEnhance(
            this.getLinkWithQueryParams(ROUTE_DISCOVER_SEARCH, {
              ...params,
              keywords: value.id,
            }),
            options,
          );

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: routeEnhanced.pathname,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          navigate(routeEnhanced);
          break;
        case 'genre':
          routeEnhanced = routeEnhance(
            this.getLinkWithQueryParams(ROUTE_DISCOVER_SEARCH, {
              ...params,
              genres: value.slug?.toLowerCase() || value.title?.toLowerCase(),
            }),
            options,
          );

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: routeEnhanced.pathname,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          navigate(routeEnhanced);
          break;
        case 'show':
        case 'movie':
          if (type === 'show') {
            routeEnhanced = routeEnhance(
              getLink(ROUTE_SHOWS_AND_MOVIES_SHOW, { show_id: value.slug || value.id }),
              options,
            );
          } else if (type === 'movie') {
            routeEnhanced = routeEnhance(
              getLink(ROUTE_SHOWS_AND_MOVIES_MOVIE, { movie_id: value.slug || value.id }),
              options,
            );
          }

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: routeEnhanced.pathname,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          navigate(routeEnhanced);

          break;
        case 'link':
          routeEnhanced = routeEnhance(value.link, options);

          analyticsInternalLinks({
            unique_id: routeEnhanced.state.unique_id,
            from_page_path: routeEnhanced.state.from_page_path,
            from_search_params: routeEnhanced.state.from_search_params,
            to_page_path: value.link,
            to_search_params: routeEnhanced.search,
            partner_id: routeEnhanced.state.partner_id,
            location: routeEnhanced.state.location,
          });

          if (value.link) window.open(value.link, '_self');
          break;

        case 'show more':
          routeEnhanced = this.getLinkWithQueryParams(ROUTE_DISCOVER_SEARCH, {
            ...params,
            search: searchPrevString,
          });

          navigate(routeEnhanced);

          break;

        case 'show more enter':
          routeEnhanced = this.getLinkWithQueryParams(ROUTE_DISCOVER_SEARCH, {
            ...filterInitialParams,
            search: value.value,
          });

          navigate(routeEnhanced);

          break;

        default:
          break;
      }

      handleOpen(false);
    }
  };

  handleReset = () => {
    const { params } = this.state;

    if (JSON.stringify(params) === JSON.stringify(filterInitialParams)) {
      return;
    }

    customAnalyticsEvent('Discover - Filter - Reset', {
      category: 'Clicked Reset',
    });

    this.setState({ params: filterInitialParams });
  };

  toggleFilters = () => {
    const { openFilters } = this.state;

    this.setState({ openFilters: !openFilters });
  };

  handleUpdateAppStatus = (data, status) => {
    const { appListUpdateAppStatuses, isAuth, partner, user } = this.props;
    const apps = [{ id: data.id, status }];

    if (status) {
      if (isAuth) {
        appListUpdateAppStatuses(apps);
      } else {
        this.getDiscoverCollections();
      }

      if (status === appsListStatuses.have) {
        analyticsUpdateAppListStatus('Added App To Profile', {
          app_id: data.id,
          app_name: data.name,
          user_id: user?.id || '',
          location: LOCATIONS_ON_CARD,
          partner_id: partner.id || 0,
        });
      }
    } else {
      appListUpdateAppStatuses(apps);
    }
  };

  handleOpenOnboarding = () => {
    const { onboardingSetState, appListUpdateAppStatuses, getDiscoverCollections } = this.props;

    onboardingSetState(true, {
      callback: data => {
        appListUpdateAppStatuses(data.apps);

        if (data.apps.length) {
          getDiscoverCollections();
        }
      },
    });
  };

  handleChangeHelmetData = () => {
    if (this.lastPath !== window.location.pathname) {
      analyticsPageLoaded();
      this.lastPath = window.location.pathname;
    }
  };

  render() {
    const { openFilters, search, params } = this.state;
    const {
      t,
      smDown,
      isSimpleView,
      isAuth,
      section1,
      section2,
      section3,
      filters,
      section1Loadings,
      section2Loadings,
      section3Loadings,
      loadingFilters,
      partner,
      partnerLoading,
      isIframe,
      searchData,
      searchLoading,
      searchPrevString,
    } = this.props;

    const isPartner = !!partner.slug;
    const advancedSearchLink = this.getLinkWithQueryParams(ROUTE_DISCOVER_SEARCH, params);
    const sectionKeys = Object.keys(section1);
    const itemList = sectionKeys.length ? section1[sectionKeys[0]] : {};

    const seoData = {
      canonicalUrl: `${siteUrl}${ROUTE_DISCOVER}`,
      ogUrl: isPartner
        ? `${siteUrl}${getLink(ROUTE_PARTNER_DISCOVER, { partner: partner.slug })}`
        : `${siteUrl}${ROUTE_DISCOVER}`,
      title: 'Discover Streaming TV',
      description: 'Discover the best Streaming Apps for you',
      itemList,
    };

    const bannerAddAppsProps = {
      title: t('streamingMarketplace.banners.onboarding.title'),
      subtitle: t('streamingMarketplace.banners.onboarding.subtitle'),
      btnText: t('buttons.addApps'),
      btnProps: {
        onClick: this.handleOpenOnboarding,
      },
    };

    return (
      <Layout>
        {!partnerLoading && (
          <Helmet
            onChangeClientState={this.handleChangeHelmetData}
            script={[getOrgSchema('wepPage', seoData)]}
            titleTemplate="%s | MyBundle"
          >
            <title>{seoData.title}</title>
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            <meta property="og:description" content={seoData.description} />
            <meta name="description" content={seoData.description} />
          </Helmet>
        )}
        <Wrapper className="streaming-marketplace">
          <ContentHero
            className="streaming-marketplace__content-hero"
            title={partner?.text_overwrites?.marketplace_title || t('streamingMarketplace.title')}
            subtitle={
              partner?.text_overwrites?.marketplace_subtitle || t('streamingMarketplace.subtitle')
            }
            hideBreadcrumbs={isIframe}
          >
            <Grid
              className="streaming-marketplace__filters filters"
              container
              spacing={smDown ? 2 : 4}
              wrap="nowrap"
            >
              <Grid item xs>
                <SearchAutoComplete
                  inputValue={search}
                  freeSolo={!search}
                  onInputChange={this.handleSearch}
                  onChange={this.handleSelect}
                  options={search ? searchData : []}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      this.debouncedSearch.cancel();
                      this.getDiscoverSearch(e.target.value);
                    }
                  }}
                  loading={searchLoading}
                  openOnFocus
                  searchPrevString={searchPrevString}
                  searchPlaceholder={
                    (partner.slug && !partner?.search_movies_shows) || isSimpleView
                      ? t('searchAutoComplete.placeholder2')
                      : t('searchAutoComplete.placeholder')
                  }
                  TextFieldProps={{
                    className: 'filters__search',
                    variant: 'filled',
                    InputProps: {
                      startAdornment: null,
                      endAdornment: (
                        <InputAdornment className="filters__search__icon" position="start">
                          {searchLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <i className="icon-search" />
                          )}
                        </InputAdornment>
                      ),
                    },
                  }}
                  analyticsLocation={LOCATIONS_DISCOVER_PAGE}
                />
              </Grid>
              <Grid item>
                <Hidden xsDown>
                  <Button
                    className="filters__advanced-button"
                    variant="contained"
                    color="secondary"
                    endIcon={<ZoomInIcon />}
                    onClick={this.toggleFilters}
                    disabled={!filters}
                  >
                    {t('buttons.advancedSearch')}
                  </Button>
                </Hidden>
                <Hidden smUp>
                  <Button
                    className="filters__advanced-button"
                    variant="contained"
                    color="secondary"
                    onClick={this.toggleFilters}
                    disabled={!filters}
                  >
                    <ZoomInIcon fontSize="inherit" />
                  </Button>
                </Hidden>
              </Grid>
            </Grid>
          </ContentHero>
          {filters && (
            <Filters
              openFilters={openFilters}
              toggleFilters={this.toggleFilters}
              values={params}
              options={{ ...filters, types }}
              handleChange={this.handleChange}
              handleReset={this.handleReset}
              loadingFilters={loadingFilters}
              onChangeSearch={this.onChangeSearch}
              link={advancedSearchLink}
            />
          )}
          <div className="streaming-marketplace__container section section--grey">
            <div className="page-content">
              {isAuth && !!partner.slug && <PartnerInternetOffer partnerBundleSlugs={['epb']} />}
              <Swimlanes
                className="streaming-marketplace__carousel"
                data={section1}
                loadings={section1Loadings}
                loadingRows={3}
                loadingType="app"
                analyticsLocation={LOCATIONS_DISCOVER_PAGE}
                handleUpdateAppStatus={this.handleUpdateAppStatus}
                bannerAddAppsProps={bannerAddAppsProps}
              />
              <Swimlanes
                className="streaming-marketplace__carousel"
                data={section2}
                loadings={section2Loadings}
                loadingRows={3}
                loadingType="app"
                analyticsLocation={LOCATIONS_DISCOVER_PAGE}
                handleUpdateAppStatus={this.handleUpdateAppStatus}
                bannerAddAppsProps={bannerAddAppsProps}
              />
              <Swimlanes
                className="streaming-marketplace__carousel"
                data={section3}
                loadings={section3Loadings}
                loadingRows={3}
                loadingType="app"
                analyticsLocation={LOCATIONS_DISCOVER_PAGE}
                handleUpdateAppStatus={this.handleUpdateAppStatus}
                bannerAddAppsProps={bannerAddAppsProps}
              />
            </div>
          </div>
        </Wrapper>
      </Layout>
    );
  }
}

StreamingMarketplace.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  smDown: PropTypes.bool.isRequired,
  searchData: PropTypes.array.isRequired,
  searchLoading: PropTypes.bool.isRequired,
  searchPrevString: PropTypes.string.isRequired,
  isSimpleView: PropTypes.bool.isRequired,
  isAuth: PropTypes.bool.isRequired,
  section1: PropTypes.object,
  section2: PropTypes.object,
  section3: PropTypes.object,
  filters: PropTypes.object,
  section1Loadings: PropTypes.object.isRequired,
  section2Loadings: PropTypes.object.isRequired,
  section3Loadings: PropTypes.object.isRequired,
  loadingFilters: PropTypes.bool.isRequired,
  getDiscoverCollections: PropTypes.func.isRequired,
  getDiscoverFilters: PropTypes.func.isRequired,
  getDiscoverSearch: PropTypes.func.isRequired,
  cleanDiscoverSearch: PropTypes.func.isRequired,
  partner: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
  isIframe: PropTypes.bool.isRequired,
  appListUpdateAppStatuses: PropTypes.func.isRequired,
  onboardingSetState: PropTypes.func.isRequired,
  userGetApps: PropTypes.func.isRequired,
  swimlanesClear: PropTypes.func.isRequired,
};

const mapStateToProps = ({ app, auth, discover, partner, swimlanes }) => ({
  isSimpleView: app.isSimpleView,
  isIframe: app.isIframe,
  isAuth: auth.isAuth,
  section1: swimlanes.section1[MARKETPLACE_PAGE] || {},
  section2: swimlanes.section2[MARKETPLACE_PAGE] || {},
  section3: swimlanes.section3[MARKETPLACE_PAGE] || {},
  filters: discover.filters,
  searchData: discover.search.data,
  searchPrevString: discover.search.string,
  section1Loadings: swimlanes.loadings.section1[MARKETPLACE_PAGE] || {},
  section2Loadings: swimlanes.loadings.section2[MARKETPLACE_PAGE] || {},
  section3Loadings: swimlanes.loadings.section3[MARKETPLACE_PAGE] || {},
  loadingFilters: discover.loadings.filters,
  searchLoading: discover.loadings.search,
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
});

const mapDispatchToProps = dispatch => {
  return {
    getDiscoverCollections: () => {
      dispatch(DiscoverActions.getDiscoverCollections.action());
    },
    getDiscoverSearch: params => dispatch(DiscoverActions.getDiscoverSearch.action({ params })),
    cleanDiscoverSearch: () => dispatch(DiscoverActions.cleanDiscoverSearch.action()),
    getDiscoverFilters: () => dispatch(DiscoverActions.getDiscoverFilters.action()),
    appListUpdateAppStatuses: apps => {
      dispatch(AppDetailsActions.appListUpdateAppStatuses.action({ apps }));
    },
    onboardingSetState: (state, props) => {
      dispatch(OnboardingActions.onboardingSetState.action({ state, props }));
    },
    userGetApps: (params = {}) => dispatch(UserActions.userGetApps.action({ params })),
    swimlanesClear: () => dispatch(SwimlanesActions.swimlanesClear.action()),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
  withRouter,
  withMediaQuery({
    smDown: theme => theme.breakpoints.down('sm'),
  }),
)(StreamingMarketplace);
